import React, { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { RadioGroup, RadioGroupItem } from "../../../../shadcn/ui/radio-group";
import { Switch } from "../../../../shadcn/ui/switch";
import { Badge } from "../../../../shadcn/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import { HelpCircle } from "lucide-react";

import { getUser } from "../../../../services/localService";
import { useCurrencies } from "../../../../hooks/useCurrencies";
import { useUserCurrency } from "../../../../hooks/useUserCurrency";

const FieldTip = ({ children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="w-4 h-4 ml-1 text-gray-400 cursor-help" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs">
      {children}
    </TooltipContent>
  </Tooltip>
);

const PricingBookingRulesCard = ({ control, errors, isReadOnly = false }) => {
  // Check if user is HOST_PROPERTY_DISTRIBUTION organization type
  const currentUser = getUser();
  const isPropertyDistribution = currentUser?.organization?.type?.code === "HOST_PROPERTY_DISTRIBUTION";

  // Load currencies from Redux and get user's currency
  const { currencies, currenciesLoading } = useCurrencies();
  const userCurrencyCode = useUserCurrency();
  const userCurrency = currencies.find(c => c.code === userCurrencyCode);

  const isPublicValue = useWatch({ control, name: "isPublic" });

  // Auto-set currency to user's currency when it changes
  useEffect(() => {
    if (userCurrencyCode && control) {
      control._formValues.currency = userCurrencyCode.toLowerCase();
    }
  }, [userCurrencyCode, control]);

  // Helper function to get duration-specific text
  const getDurationText = duration => {
    switch (duration) {
      case "hourly":
        return {
          singular: "hour",
          plural: "hours",
          unit: "hourly",
        };
      case "daily":
        return {
          singular: "day",
          plural: "days",
          unit: "daily",
        };
      case "weekly":
        return {
          singular: "week",
          plural: "weeks",
          unit: "weekly",
        };
      case "monthly":
        return {
          singular: "month",
          plural: "months",
          unit: "monthly",
        };
      default:
        return {
          singular: "hour",
          plural: "hours",
          unit: "hourly",
        };
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">Pricing & Booking Rules</CardTitle>
          </div>
          <CardDescription className="text-sm text-gray-500 mt-1">How it can be booked</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <TooltipProvider>
          {/* Booking Duration */}
          <div>
            <Label className="flex items-center mb-2">
              Booking Duration{" "}
              <FieldTip>
                Set how customers book — by the hour, day, week, or month. Determines the pricing rate period.
              </FieldTip>
            </Label>
            <Controller
              name="bookingDuration"
              control={control}
              render={({ field }) => (
                <RadioGroup value={field.value} onValueChange={field.onChange} className="flex space-x-4">
                  {["hourly", "daily", "weekly", "monthly"].map(option => (
                    <div key={option} className="flex items-center space-x-2">
                      <RadioGroupItem value={option} id={`duration-${option}`} disabled={isReadOnly} />
                      <Label htmlFor={`duration-${option}`} className="font-normal">
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            />
            {errors.bookingDuration && <p className="text-sm text-red-500 mt-1">{errors.bookingDuration.message}</p>}
          </div>

          {/* Base Price */}
          <div>
            <Label htmlFor="base-price" className="flex items-center mb-1">
              Base Price{" "}
              <FieldTip>
                The cost per duration period. Customers are charged this amount for each booking unit.
              </FieldTip>
            </Label>
            <div className="flex items-center gap-2">
              <div className="w-[152px] px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700 flex items-center gap-2">
                <span>{userCurrency?.code || userCurrencyCode || "KES"}</span>
                {userCurrency?.symbol && (
                  <Badge variant="secondary" className="text-xs bg-white">
                    {userCurrency.symbol}
                  </Badge>
                )}
              </div>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name="basePrice"
                control={control}
                render={({ field }) => (
                  <Controller
                    name="bookingDuration"
                    control={control}
                    render={({ field: durationField }) => {
                      const durationText = getDurationText(durationField.value);
                      return (
                        <Input
                          id="base-price"
                          placeholder={`Price per ${durationText.singular}`}
                          type="number"
                          className="flex-1"
                          disabled={isReadOnly}
                          {...field}
                        />
                      );
                    }}
                  />
                )}
              />
            </div>
            {errors.basePrice && <p className="text-sm text-red-500 mt-1">{errors.basePrice.message}</p>}
            <p className="text-xs text-gray-500 mt-1">Currency is based on your country setting</p>
          </div>

          {/* Duration Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="min-duration" className="flex items-center mb-1">
                Minimum Duration{" "}
                <FieldTip>The shortest time a customer can book this space in a single reservation.</FieldTip>
              </Label>
              <Controller
                name="minDuration"
                control={control}
                render={({ field }) => (
                  <Controller
                    name="bookingDuration"
                    control={control}
                    render={({ field: durationField }) => {
                      const durationText = getDurationText(durationField.value);
                      return (
                        <Input
                          id="min-duration"
                          placeholder={`Min ${durationText.plural}`}
                          type="number"
                          disabled={isReadOnly}
                          {...field}
                        />
                      );
                    }}
                  />
                )}
              />
              {errors.minDuration && <p className="text-sm text-red-500 mt-1">{errors.minDuration.message}</p>}
            </div>
            <div>
              <Label htmlFor="max-duration" className="flex items-center mb-1">
                Maximum Duration{" "}
                <FieldTip>The longest time a customer can book this space in a single reservation.</FieldTip>
              </Label>
              <Controller
                name="maxDuration"
                control={control}
                render={({ field }) => (
                  <Controller
                    name="bookingDuration"
                    control={control}
                    render={({ field: durationField }) => {
                      const durationText = getDurationText(durationField.value);
                      return (
                        <Input
                          id="max-duration"
                          placeholder={`Max ${durationText.plural}`}
                          type="number"
                          disabled={isReadOnly}
                          {...field}
                        />
                      );
                    }}
                  />
                )}
              />
              {errors.maxDuration && <p className="text-sm text-red-500 mt-1">{errors.maxDuration.message}</p>}
            </div>
          </div>

          {/* Visibility */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="visibility" className="flex items-center">
                  Visibility{" "}
                  <FieldTip>
                    Toggle ON to make this space visible on the SPACIA marketplace. When OFF, only you can see it.
                  </FieldTip>
                </Label>
                <p className="text-sm text-gray-500">
                  {isPropertyDistribution
                    ? "Always Public - Property Distribution spaces are automatically visible on SPACIA marketplace"
                    : "Public - On SPACIA marketplace"}
                </p>
              </div>
              {!isPropertyDistribution && (
                <Controller
                  name="isPublic"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="visibility"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isReadOnly}
                    />
                  )}
                />
              )}
              {isPropertyDistribution && (
                <div className="flex items-center px-3 py-2 bg-green-100 text-green-800 rounded-md text-sm font-medium">
                  Always Public
                </div>
              )}
            </div>
            {errors.isPublic && <p className="text-sm text-red-500 mt-1">{errors.isPublic.message}</p>}
          </div>

          {/* Visible To - only reveal when visibility is public */}
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              isPublicValue || isPropertyDistribution ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <Label className="flex items-center">
                Visible To{" "}
                <FieldTip>Choose which customer types can see and book this space when it's set to Public.</FieldTip>
              </Label>
              <div className="space-y-4 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p>Corporate customers</p>
                    <p className="text-sm text-gray-500">Show on corporate dashboard &amp; booking</p>
                  </div>
                  <Controller
                    name="visibleToCorporate"
                    control={control}
                    render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isReadOnly} />
                    )}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p>Consumer customers</p>
                    <p className="text-sm text-gray-500">Show on consumer marketplace &amp; booking</p>
                  </div>
                  <Controller
                    name="visibleToConsumer"
                    control={control}
                    render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isReadOnly} />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default PricingBookingRulesCard;
